import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    percentage: Number,
    label: String,
    total: Number
  }

  connect() {
    this.animationProgress = 0
    this.animateGauge()
    
    // Redraw on window resize for responsiveness
    this.resizeHandler = () => this.drawGauge(1) // No animation on resize
    window.addEventListener('resize', this.resizeHandler)
  }

  animateGauge() {
    const duration = 1200 // Animation duration in ms
    const startTime = performance.now()
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      
      // Easing function (ease-out)
      this.animationProgress = 1 - Math.pow(1 - progress, 3)
      
      this.drawGauge(this.animationProgress)
      
      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }
    
    requestAnimationFrame(animate)
  }

  disconnect() {
    window.removeEventListener('resize', this.resizeHandler)
  }

  drawGauge(animationProgress = 1) {
    const canvas = this.element
    const ctx = canvas.getContext('2d')
    
    // Set canvas size based on container (responsive)
    const container = canvas.parentElement
    const size = Math.min(container.clientWidth, 400)
    canvas.width = size
    canvas.height = size * 0.7 // Height is 70% of width for semi-circle
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    
    // Calculate dimensions
    const centerX = canvas.width / 2
    const centerY = canvas.height - 20 // Position arc near bottom
    const radius = Math.min(centerX, centerY) - 10 // Wider radius
    const progressLineWidth = 10 // Thinner green arc
    const backgroundLineWidth = 8 // Thinner gray arc
    
    // Arc settings
    const startAngle = Math.PI // Start at left (180°)
    const endAngle = 2 * Math.PI // End at right (360°/0°)
    const targetProgressAngle = startAngle + (endAngle - startAngle) * (this.percentageValue / 100)
    const progressAngle = startAngle + (targetProgressAngle - startAngle) * animationProgress
    
    // Draw background arc (gray) - thinner
    ctx.beginPath()
    ctx.arc(centerX, centerY, radius, startAngle, endAngle)
    ctx.strokeStyle = '#4B5563' // Gray-600
    ctx.lineWidth = backgroundLineWidth
    ctx.lineCap = 'round'
    ctx.stroke()
    
    // Draw progress arc (lime green) - thicker
    ctx.beginPath()
    ctx.arc(centerX, centerY, radius, startAngle, progressAngle)
    ctx.strokeStyle = '#A3E635' // Lime-400
    ctx.lineWidth = progressLineWidth
    ctx.lineCap = 'round'
    ctx.stroke()
    
    // Draw dot at the end of progress arc
    const dotX = centerX + radius * Math.cos(progressAngle)
    const dotY = centerY + radius * Math.sin(progressAngle)
    
    ctx.beginPath()
    ctx.arc(dotX, dotY, 10, 0, 2 * Math.PI)
    ctx.fillStyle = '#A3E635'
    ctx.fill()
    
    // Optional: Add inner shadow/glow to dot
    ctx.beginPath()
    ctx.arc(dotX, dotY, 6, 0, 2 * Math.PI)
    ctx.fillStyle = '#84CC16' // Darker lime
    ctx.fill()
  }
}
